import { AIRFRAME_DEFAULT, ATMO, FLIGHT } from './tuning';

/**
 * Airframe definitions.
 *
 * The raw numbers live in tuning.ts (SPEC Appendix A). This module turns them into the runtime
 * shape the flight model wants: degrees converted to radians once, and the quantities that are
 * pure algebra on the others — aspect ratio, the induced-drag factor, cruise dynamic pressure,
 * the 1-g stall speed — derived rather than restated, so they cannot drift out of agreement.
 *
 * Units: metres, m/s, kg, newtons, radians, seconds, pascals.
 */

const DEG_TO_RAD = Math.PI / 180;

export interface Airframe {
  readonly name: string;

  // --- Mass and geometry
  readonly massKg: number;
  readonly wingAreaM2: number;
  readonly wingSpanM: number;
  /** Body-axis moments of inertia [roll, pitch, yaw], kg·m². Used for Euler cross-coupling. */
  readonly inertia: readonly [number, number, number];

  // --- Lift
  readonly cl0: number;
  /** Lift-curve slope, per radian. */
  readonly clAlpha: number;
  readonly clMax: number;
  readonly alphaStallRad: number;

  // --- Drag and side force
  readonly cd0: number;
  readonly oswaldE: number;
  /** Cy_β, per radian. Negative: side force opposes sideslip. */
  readonly cyBeta: number;

  // --- Propulsion
  readonly thrustMaxN: number;
  readonly thrustLagS: number;
  readonly fuelKg: number;
  readonly fuelBurnKgS: number;

  // --- Control response, rad/s² per unit command at unit authority (see FLIGHT in tuning.ts)
  readonly rollAuthority: number;
  readonly pitchAuthority: number;
  readonly yawAuthority: number;
  /** Rate damping, 1/s. */
  readonly rollDamp: number;
  readonly pitchDamp: number;
  readonly yawDamp: number;

  // --- Limits
  readonly gLimitPos: number;
  readonly gLimitNeg: number;
  readonly gFailure: number;
  readonly vneMs: number;
  readonly vCruiseMs: number;
  /** The design stall speed from Appendix A. See vStall1gMs for what the model actually gives. */
  readonly vStallDesignMs: number;

  // --- Signatures (unused until M5)
  readonly irSignature: number;
  readonly rcsM2: number;

  // --- Derived
  /** b²/S. */
  readonly aspectRatio: number;
  /** k in CD = CD0 + k·CL², from 1/(π·e·AR). */
  readonly inducedDragK: number;
  /** Dynamic pressure at cruise, Pa — the normaliser for control authority. */
  readonly dynamicPressureCruisePa: number;
  /** Peak achievable lift coefficient, which occurs at alphaStall. */
  readonly clAtStall: number;
  /** 1-g stall speed at sea level, m/s, implied by the aerodynamics above. */
  readonly vStall1gMs: number;
  /** Post-stall band width, radians. */
  readonly postStallRangeRad: number;
  readonly deepStallAlphaRad: number;
}

type AirframeConstants = typeof AIRFRAME_DEFAULT;

/** Build a runtime airframe from an Appendix A constant block. */
export function defineAirframe(name: string, constants: AirframeConstants): Airframe {
  const alphaStallRad = constants.alphaStallDeg * DEG_TO_RAD;
  const aspectRatio = (constants.wingSpanM * constants.wingSpanM) / constants.wingAreaM2;
  const inducedDragK = 1 / (Math.PI * constants.oswaldE * aspectRatio);
  const clAtStall = constants.cl0 + constants.clAlpha * alphaStallRad;

  // V_s = √(2·m·g / (ρ₀·S·CL_max)). Derived, not restated: this is the speed the aerodynamics
  // actually stall at, and everything that depends on stall margin must use it.
  const vStall1gMs = Math.sqrt(
    (2 * constants.massKg * ATMO.G) / (ATMO.RHO_0 * constants.wingAreaM2 * clAtStall),
  );

  return {
    name,
    massKg: constants.massKg,
    wingAreaM2: constants.wingAreaM2,
    wingSpanM: constants.wingSpanM,
    inertia: constants.inertia,

    cl0: constants.cl0,
    clAlpha: constants.clAlpha,
    clMax: constants.clMax,
    alphaStallRad,

    cd0: constants.cd0,
    oswaldE: constants.oswaldE,
    cyBeta: constants.cyBeta,

    thrustMaxN: constants.thrustMaxN,
    thrustLagS: constants.thrustLagS,
    fuelKg: constants.fuelKg,
    fuelBurnKgS: constants.fuelBurnKgS,

    rollAuthority: constants.rollAuthority,
    pitchAuthority: constants.pitchAuthority,
    yawAuthority: constants.yawAuthority,
    rollDamp: constants.rollDamp,
    pitchDamp: constants.pitchDamp,
    yawDamp: constants.yawDamp,

    gLimitPos: constants.gLimitPos,
    gLimitNeg: constants.gLimitNeg,
    gFailure: constants.gFailure,
    vneMs: constants.vneMs,
    vCruiseMs: constants.vCruiseMs,
    vStallDesignMs: constants.vStallMs,

    irSignature: constants.irSignature,
    rcsM2: constants.rcsM2,

    aspectRatio,
    inducedDragK,
    dynamicPressureCruisePa: 0.5 * ATMO.RHO_0 * constants.vCruiseMs * constants.vCruiseMs,
    clAtStall,
    vStall1gMs,
    postStallRangeRad: FLIGHT.postStallRangeDeg * DEG_TO_RAD,
    deepStallAlphaRad: FLIGHT.deepStallAlphaDeg * DEG_TO_RAD,
  };
}

/** The v1 airframe. SPEC §10 recommends shipping one and adding unlockables only if ahead. */
export const AIRFRAME_SCOUT: Airframe = defineAirframe('SCOUT', AIRFRAME_DEFAULT);

/** Airframes by name, for mission loadouts at M8. */
export const AIRFRAMES: Readonly<Record<string, Airframe>> = {
  [AIRFRAME_SCOUT.name]: AIRFRAME_SCOUT,
};
